import { CURRENCY } from './settings';

/**
 * Get currency prefix.
 */
const getPrefix = (symbol, symbolPosition) => {
    const prefixes = {
        left: symbol,
        left_space: ' ' + symbol,
        right: '',
        right_space: '',
    };
    return prefixes[symbolPosition] || '';
};

/**
 * Get currency suffix.
 */
const getSuffix = (symbol, symbolPosition) => {
    const suffixes = {
        left: '',
        left_space: '',
        right: symbol,
        right_space: ' ' + symbol,
    };
    return suffixes[symbolPosition] || '';
};

/**
 * Currency information in normalized format from server settings.
 */
const siteCurrencySettings = {
    code: CURRENCY.code,
    symbol: CURRENCY.symbol,
    thousandSeparator: CURRENCY.thousandSeparator,
    decimalSeparator: CURRENCY.decimalSeparator,
    minorUnit: CURRENCY.precision,
    prefix: getPrefix(CURRENCY.symbol, CURRENCY.symbolPosition),
    suffix: getSuffix(CURRENCY.symbol, CURRENCY.symbolPosition),
};

/**
 * Gets currency information in normalized format from an API response or the server.
 *
 * If no currency was provided, or currency_code is empty, the default store currency will be used.
 */
export const getCurrencyFromPriceResponse = (currencyData) => {
    if (!currencyData?.currency_code) {
        return siteCurrencySettings;
    }

    const {
        currency_code: code,
        currency_symbol: symbol,
        currency_thousand_separator: thousandSeparator,
        currency_decimal_separator: decimalSeparator,
        currency_minor_unit: minorUnit,
        currency_prefix: prefix,
        currency_suffix: suffix,
    } = currencyData;

    return {
        code: code || 'USD',
        symbol: symbol || '$',
        thousandSeparator: typeof thousandSeparator === 'string' ? thousandSeparator : ',',
        decimalSeparator: typeof decimalSeparator === 'string' ? decimalSeparator : '.',
        minorUnit: Number.isFinite(minorUnit) ? minorUnit : 2,
        prefix: typeof prefix === 'string' ? prefix : '$',
        suffix: typeof suffix === 'string' ? suffix : '',
    };
};

/**
 * Gets currency information in normalized format, allowing overrides.
 */
export const getCurrency = (currencyData) => {
    return {
        ...siteCurrencySettings,
        ...currencyData,
    };
};

const applyThousandSeparator = (numberString, thousandSeparator) => {
    return numberString.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
};

const splitDecimal = (numberString) => {
    const parts = numberString.split('.');
    const beforeDecimal = parts[0];
    const afterDecimal = parts[1] || '';
    return {
        beforeDecimal,
        afterDecimal,
    };
};

const applyDecimal = (afterDecimal, decimalSeparator, minorUnit) => {
    if (afterDecimal) {
        return `${decimalSeparator}${afterDecimal.padEnd(minorUnit, '0')}`;
    }

    if (minorUnit > 0) {
        return `${decimalSeparator}${'0'.repeat(minorUnit)}`;
    }

    return '';
};

export const formatPrice = (price, currencyData) => {
    if (price === '' || price === undefined) {
        return '';
    }

    const priceInt = typeof price === 'number' ? price : parseInt(price, 10);

    if (!Number.isFinite(priceInt)) {
        return '';
    }

    const currency = getCurrency(currencyData);

    const { minorUnit, prefix, suffix, decimalSeparator, thousandSeparator } = currency;

    const formattedPrice = priceInt / 10 ** minorUnit;

    const { beforeDecimal, afterDecimal } = splitDecimal(formattedPrice.toString());

    const formattedValue = `${prefix}${applyThousandSeparator(beforeDecimal, thousandSeparator)}${applyDecimal(
        afterDecimal,
        decimalSeparator,
        minorUnit,
    )}${suffix}`;

    // This uses a textarea to magically decode HTML currency symbols.
    const txt = document.createElement('textarea');
    txt.innerHTML = formattedValue;
    return txt.value;
};
